import { collection, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, uploadBytes } from "firebase/storage";
import { ref } from "process";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import { db, storage } from "../firebase-key";
import { useAuth } from "../state/appContext";



const documentSections = {
    customer_kyc: [
        "PHOTO", "PAN", "AADHAR", "LAND DOCUMENTS", "RENT / LEASE DOCUMENTS",
        "MORTGAGE DOCUMENTS", "ELECTRICITY BILL", "JATI/MINORITY CERTIFICATE",
        "MOOL NIWAS", "LIC RECEIPT", "STAMP OF FIRM", "MARKSHEET"
    ],
    business_kyc: [
        "UDYAM REGISTRATION", "BRN REGISTRATION", "GST REGISTRATION",
        "FSSAI REGISTRATION", "MSME REGISTRATION", "GOOGLE LOCATION",
        "LAND PHOTO", "LAND 360 VIDEO", "1ST SALE BILL", "GST DEPOSIT RECEIPT",
        "LETTERHEAD SEAL SIGN", "MANDI LICENSE"
    ],
    banking: [
        "INCOME TAX RETURN - 3 YEARS", "BANK STATEMENT - 1 YEAR", "PROJECT REPORT",
        "CMA DATA", "PLANT & MACH QUOTATION", "BUILDING QUOTATION",
        "CANCELLED CHEQUE", "GST RETURN (1 YEAR)", "AUDIT REPORT"
    ],
    bank_related: [
        "APPLICATION FORM", "ASSETS & LIABILITIES", "PROPERTY LEGAL REPORT",
        "VALUATION REPORT", "FI REPORT", "SANCTION LETTER"
    ]
};

const UserDocuments = () => {
    const { user } = useAuth();
    const [docs, setDocs] = useState({});
    const [uploading, setUploading] = useState(false);

    const loadDocuments = useCallback(async () => {
        if (!user?.uid) return;

        const userDocsPath = collection(db, "users", user.uid, "documents");
        const snapshot = await getDocs(userDocsPath);

        const collected = {};
        snapshot.forEach((d) => {
            collected[d.id] = d.data();
        });

        setDocs(collected);
    }, [user.uid]);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const uploadDocument = async (section, name, file) => {
        if (!file) return;

        setUploading(true);

        const storageRef = ref(storage, `user_documents/${user.uid}/${section}/${name}.pdf`);

        try {
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            await setDoc(
                doc(db, "users", user.uid, "documents", `${section}__${name}`),
                {
                    name,
                    section,
                    url,
                    updated_at: new Date()
                }
            );

            toast.success(`${name} uploaded ✔`);
            loadDocuments();
        } catch (err) {
            toast.error("Upload failed");
            console.log(err);
        }
        setUploading(false);
    };

    return (
        <div className="p-6 bg-white rounded-xl shadow-md border">
            <h2 className="text-2xl font-semibold mb-4">📄 Your Documents</h2>

            {Object.entries(documentSections).map(([section, items]) => (
                <div key={section} className="mb-10">
                    <h3 className="text-xl font-bold mb-3 capitalize">{section.replace("_", " ")}</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

                        {items.map((docName) => {
                            const key = `${section}__${docName}`;
                            const existing = docs[key];

                            return (
                                <div key={docName} className="p-4 border rounded-md bg-gray-50">
                                    <p className="font-semibold">{docName}</p>

                                    {/* Upload */}
                                    <input
                                        type="file"
                                        disabled={uploading}
                                        accept="application/pdf"
                                        onChange={(e) => uploadDocument(section, docName, e.target.files[0])}
                                        className="mt-2 text-sm"
                                    />

                                    {/* Download */}
                                    {existing?.url && (
                                        <a
                                            href={existing.url}
                                            target="_blank"
                                            className="text-blue-600 underline text-sm mt-2 block" rel="noreferrer"
                                        >
                                            Download
                                        </a>
                                    )}
                                </div>
                            );
                        })}

                    </div>
                </div>
            ))}
        </div>
    );
};


const NewUser = () => {
    const { user, loading } = useAuth();
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("info");

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm();

    // 🟦 Load user data into form
    useEffect(() => {
        if (user) {
            reset(user);
        }
    }, [user, reset]);

    const onSubmit = async (data) => {
        if (!user) return;

        setSaving(true);
        try {
            await updateDoc(doc(db, "users", user.uid), {
                ...data,
                updated_at: new Date(),
            });

            toast.success("Profile updated successfully! 🎉");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update profile");
        }
        setSaving(false);
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center text-gray-700 text-lg">
                Loading profile...
            </div>
        );
    }

    return (
        <div className="min-h-100 py-28">
            <ToastContainer />
            <div className="w-full flex justify-center mb-8">
                <div className="flex bg-gray-100 p-1 rounded-xl shadow-sm">

                    <button
                        onClick={() => setTab("info")}
                        className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200
        ${tab === "info"
                                ? "bg-white shadow-md text-blue-600"
                                : "text-gray-600 hover:text-gray-800"
                            }`}
                    >
                        User Info
                    </button>

                    <button
                        onClick={() => setTab("docs")}
                        className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200
        ${tab === "docs"
                                ? "bg-white shadow-md text-blue-600"
                                : "text-gray-600 hover:text-gray-800"
                            }`}
                    >
                        Documents
                    </button>

                </div>
            </div>
            {tab === "info" && <div className="flex justify-center items-center bg-[#f7f9fc] p-4">
                <div className="w-full max-w-4xl bg-white p-10 rounded-2xl shadow-lg border border-gray-200">

                    <h1 className="text-3xl font-semibold text-center text-gray-800 mb-6">
                        Edit Your Profile ✏️
                    </h1>

                    <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">

                        {/* Firm Name */}
                        <InputField label="Firm Name" name="firm_name" register={register} errors={errors} />

                        {/* Project Work */}
                        <InputField label="Project Work" name="project_work" register={register} errors={errors} />

                        {/* Project Address */}
                        <InputField label="Project Address" name="project_address" register={register} errors={errors} />

                        {/* Loan Amount */}
                        <InputField label="Loan Amount (Lakhs)" name="loan_amount" register={register} errors={errors}
                            validate={{ pattern: /^\d+$/ }}
                        />

                        {/* Govt Scheme */}
                        <InputField label="Govt Scheme Name" name="gov_scheme_name" register={register} errors={errors} />

                        {/* Customer Name */}
                        <InputField label="Customer Name" name="customer_name" register={register} errors={errors} />

                        {/* DOB */}
                        <InputField label="DOB" name="dob" type="date" register={register} errors={errors} />

                        {/* PAN */}
                        <InputField
                            label="PAN"
                            name="pan"
                            register={register}
                            errors={errors}
                            validate={{
                                pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                            }}
                        />

                        {/* Aadhar */}
                        <InputField
                            label="Aadhaar"
                            name="aadhaar"
                            register={register}
                            errors={errors}
                            validate={{ pattern: /^\d{12}$/ }}
                        />

                        {/* Udyam */}
                        <InputField label="Udyam" name="udyam" register={register} errors={errors} />

                        {/* GST */}
                        <InputField
                            label="GST"
                            name="gst"
                            register={register}
                            errors={errors}
                            validate={{ pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/ }}
                        />

                        {/* Mobile Numbers */}
                        <InputField
                            label="Mobile No (1)"
                            name="mobile1"
                            register={register}
                            errors={errors}
                            validate={{ pattern: /^[0-9]{10}$/ }}
                        />

                        <InputField
                            label="Mobile No (2)"
                            name="mobile2"
                            register={register}
                            errors={errors}
                            validate={{ pattern: /^[0-9]{10}$/ }}
                        />

                        {/* Alternate Email */}
                        <InputField label="Alternate Email" name="email_alt" register={register} errors={errors} />

                        {/* Address */}
                        <InputField label="Address" name="address" register={register} errors={errors} />

                        {/* Pincode */}
                        <InputField
                            label="Pincode"
                            name="pincode"
                            register={register}
                            errors={errors}
                            validate={{ pattern: /^[0-9]{6}$/ }}
                        />

                        {/* Email — Cannot Edit */}
                        <div className="col-span-3">
                            <label className="text-sm text-gray-700">Email (not editable)</label>
                            <input
                                className="w-full mt-1 px-4 py-3 bg-gray-200 border rounded-lg text-gray-600 cursor-not-allowed"
                                value={user.email}
                                disabled
                            />
                        </div>

                        {/* UPDATE BUTTON */}
                        <button
                            type="submit"
                            disabled={saving}
                            className="col-span-3 w-full py-3 mt-4 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                        >
                            {saving ? "Saving..." : "Update Profile"}
                        </button>

                    </form>
                </div>
            </div>}
            {tab === "docs" && <UserDocuments />}

        </div>
    );
};

// 🔥 Reusable Input Field Component
const InputField = ({ label, name, register, errors, type = "text", validate }) => (
    <div>
        <label className="text-sm text-gray-700">{label}</label>

        <input
            type={type}
            {...register(name, {
                required: `${label} is required`,
                ...(validate?.pattern && {
                    pattern: {
                        value: validate.pattern,
                        message: `Invalid ${label}`
                    }
                }),
            })}
            className="w-full mt-1 px-4 py-3 rounded-lg bg-gray-50 border border-gray-300 outline-none"
            placeholder={label}
        />

        {errors[name] && <p className="text-red-500 text-sm">{errors[name].message}</p>}
    </div>
);

export default NewUser;
